import { WebSocketStatus, type CoreNotifyEvent } from '@/services/websocket/types'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useWebSocketStore = defineStore('websocket', () => {
    const status = ref<WebSocketStatus>(WebSocketStatus.DISCONNECTED)
    const error = ref<string | null>(null)
    const reconnectAttempts = ref(0)
    const events = ref<CoreNotifyEvent[]>([])
    const maxStoredEvents = 50

    const connected = computed(() => status.value === WebSocketStatus.CONNECTED)
    const connecting = computed(() => status.value === WebSocketStatus.CONNECTING)
    const disconnected = computed(() => status.value === WebSocketStatus.DISCONNECTED)

    const wsService = getWebSocketService()

    wsService.onStatusChange((newStatus) => {
        status.value = newStatus

        if (newStatus === WebSocketStatus.CONNECTED) {
            error.value = null
            reconnectAttempts.value = 0
        } else if (newStatus === WebSocketStatus.RECONNECTING) {
            reconnectAttempts.value++
        } else if (newStatus === WebSocketStatus.ERROR) {
            error.value = 'WebSocket connection error'
        }
    })

    wsService.onEvent((event: CoreNotifyEvent) => {
        events.value.unshift(event)

        if (events.value.length > maxStoredEvents) {
            events.value = events.value.slice(0, maxStoredEvents)
        }
    })

    async function connect(token?: string) {
        try {
            error.value = null
            await wsService.connect(token)
        } catch (err: any) {
            error.value = err.message || 'Failed to connect to WebSocket'
            throw err
        }
    }

    function disconnect() {
        wsService.disconnect()
        events.value = []
    }

    function clearEvents() {
        events.value = []
    }

    function sendTaskResult(taskId: string, result: any, taskError?: any) {
        wsService.sendTaskResult({
            taskId,
            result,
            error: taskError
        })
    }

    function setError(errorMessage: string | null) {
        error.value = errorMessage
    }

    return {
        status,
        error,
        reconnectAttempts,
        events,
        connected,
        connecting,
        disconnected,
        connect,
        disconnect,
        clearEvents,
        sendTaskResult,
        setError
    }
})


